import Typography from '@mui/material/Typography';
import { Stack, Modal, Button, TextField } from '@mui/material';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { memo, useState } from 'react';
import useToast from '../../hooks/useToast';
import { useAppSelector } from '../../store';
import { useUnresolveTraceMutation } from '../../api/traces/traces.api.rtk';
import moment from 'moment';
import useAppContext from '../../hooks/useAppContext';

/**
 * TracesUnresolveConfirmationModal Component
 *
 * This component renders a modal for confirming the action of marking a trace as unresolved.
 * It allows users to add remarks and submit the unresolved status.
 *
 * @param {Object} props - The component props
 * @param {boolean} props.isOpen - Determines if the modal is open
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {any} [props.loadingComponent] - Optional loading component
 * @param {any} [props.traceData] - Data of the trace to be unresolved
 *
 * @returns {JSX.Element} The rendered TracesUnresolveConfirmationModal component
 */
const TracesUnresolveConfirmationModal = ({
  isOpen,
  onClose,
  traceData,
}: {
  isOpen: boolean;
  onClose: () => void;
  loadingComponent?: any;
  traceData?: any;
}) => {
  const { openSecCodeDialog } = useAppContext();
  const [unresolveTrace] = useUnresolveTraceMutation();
  const { toast } = useToast();

  const selectedProperty = useAppSelector(
    (state) => state.properties.selectedProperty,
  );
  const [commentValue, setCommentValue] = useState('');
  const [error, setError] = useState(false);

  /**
   * Handles changes in the comment input field
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCommentValue(event.target.value);
    setError(false);
  };

  /**
   * Handles the submission of the unresolved trace
   */
  const handleSubmit = async () => {
    if (commentValue.trim() === '') {
      setError(true); // Set error state if input is empty
      return;
    }
    const jsonData = {
      properties: [
        {
          property: {
            propertyID: selectedProperty.value,
            traces: [
              {
                trace: {
                  traceDetailsID: traceData?.trace?.traceDetailsID,
                  remarks: commentValue,
                },
              },
            ],
          },
        },
      ],
    };

    openSecCodeDialog(async (res) => {
      if (!res?.data.isValid) return;
      await unresolveTrace(jsonData)
        .unwrap()
        .then(() => {
          toast({
            severity: 'success',
            message: 'Success on Unresolving Trace',
          });
        })
        .catch((err) =>
          toast({
            severity: 'error',
            message: err,
          }),
        )
        .finally(() => {
          setCommentValue('');
          onClose();
        });
    }, 'resolve_traces'); // edit priv
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={() => {
          onClose();
        }}
        aria-labelledby='modal-title'
        aria-describedby='modal-description'
      >
        <Stack
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%', // Set width to 90% of the viewport width
            maxWidth: 400, // Limit maximum width to 1214 pixels
            bgcolor: 'background.paper',
            boxShadow: 24,
            overflow: 'auto', // Add overflow property to enable scrolling when content overflows
            '@media (max-width: 400)': {
              width: '95%', // Adjust width for smaller screens
              maxWidth: '100%', // Remove max width restriction for smaller screens
              height: '100dvh', // Adjust height for smaller screens
              maxHeight: 'none', // Remove max height restriction for smaller screens
            },
          }}
        >
          <Stack flexDirection='row'>
            <Stack
              padding='var(--2, 16px) var(--2, 16px) var(--2, 16px) var(--2, 24px)'
              flexDirection='column'
              alignItems='flex-start'
              gap='var(--2, 10px)'
            >
              <Stack
                direction='row'
                alignItems='center'
                gap='16px'
                width='100%'
                pt={1}
              >
                <RefreshOutlinedIcon sx={{ fontSize: '35px', color: 'navy' }} />
              </Stack>
              <Stack
                direction='row'
                alignItems='center'
                gap='16px'
                width='100%'
                pt={1}
              >
                <Typography fontWeight={500} fontSize='18px'>
                  Mark Unresolved Confirmation
                </Typography>
              </Stack>
              <Stack
                direction='row'
                alignItems='center'
                gap='16px'
                width='100%'
              >
                <Typography fontSize='14px'>
                  Are you sure you want to mark this as Unresolved?
                </Typography>
              </Stack>
              <Stack
                direction='row'
                alignItems='center'
                gap='16px'
                width='100%'
              >
                <Typography color='textSecondary' fontSize='14px'>
                  Trace Target:
                </Typography>
                <Typography fontSize='14px'>
                  {traceData?.trace?.shortName}
                </Typography>
              </Stack>
              <Stack
                direction='row'
                alignItems='center'
                gap='16px'
                width='100%'
              >
                <Typography color='textSecondary' fontSize='14px'>
                  Date/Time:
                </Typography>
                <Typography fontSize='14px'>
                  {traceData?.trace?.date}{' '}
                  {moment(traceData?.trace?.recurringTime, 'HH:mm:ss').format(
                    'HH:mm A',
                  )}
                </Typography>
              </Stack>
              <Stack
                direction='row'
                alignItems='center'
                gap='16px'
                width='100%'
              >
                <Typography color='textSecondary' fontSize='14px'>
                  Trace Message:
                </Typography>
                <Typography fontSize='14px'>
                  {traceData?.trace?.message}
                </Typography>
              </Stack>
              <Stack
                direction='row'
                alignItems='center'
                gap='16px'
                width='100%'
                pt={1}
              >
                <TextField
                  label='remarks'
                  variant='outlined'
                  multiline={true}
                  fullWidth
                  value={commentValue}
                  onChange={handleChange}
                  error={error} // Set error prop to display error state
                  helperText={error ? 'remarks cannot be empty' : ''}
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack sx={{ p: '20px', position: 'sticky', top: '0', zIndex: 1 }}>
            <Stack flexDirection='row' justifyContent='flex-end'>
              <Button
                onClick={() => {
                  onClose();
                }}
                color='primary'
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={handleSubmit}
              >
                Confirm
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Modal>
    </>
  );
};

export default memo(TracesUnresolveConfirmationModal);
